<template>
  <div>
    <div class="row pt-1">
      <div class="col-sm-1">
        <div class="row">
          <div class="col">
            <span class="data-name font-weight-normal">Año Fiscal:</span>
            <p class="data font-weight-normal">
              {{ model.fiscalYearDescription }}
            </p>
          </div>
        </div>
        <div v-if="(model.amendments || []).length > 0 && !isChild" class="row">
          <div class="col">
            <span class="color-primary-2 font-weight-normal">
              ({{ model.amendments.length }}) Enmienda(s)
            </span>
          </div>
        </div>
      </div>
      <div class="col-sm-3 col-contract-number">
        <span class="data-name font-weight-normal">Número de Contrato:</span>
        <div v-if="editingNumbers && isChild" class="d-flex align-items-center">
          <span class="font-weight-bold mr-1">{{ numberValue }}-</span>
          <custom-input
            maxlength="1"
            name="contractNumberSuffixEdit"
            style="max-width: 50px;"
            :value="numberSuffix"
            @input="
              $emit('update-number-suffix', $event.slice(0, 1).toUpperCase())
            "
          />
        </div>
        <div v-else-if="editingNumbers" class="d-flex align-items-center">
          <custom-input
            name="contractNumberEdit"
            :value="numberValue"
            @input="$emit('update-number', $event)"
          />
          <action-button
            v-if="canEditNumber"
            class="ml-2 number-edit-btn"
            i="fas fa-save"
            title="Guardar"
            variant="outline-success"
            @click="$emit('save-numbers')"
          />
          <action-button
            v-if="canEditNumber"
            class="ml-2 number-edit-btn"
            i="fas fa-times"
            title="Cancelar"
            variant="outline-secondary"
            @click="$emit('cancel-edit-numbers')"
          />
        </div>
        <p v-else class="data font-weight-normal">
          {{ model.contractNumber ? model.contractNumber : '-' }}
          <em
            v-if="canEditNumber"
            class="fas fa-edit color-primary-1 ml-1"
            style="cursor: pointer;"
            title="Editar número de contrato"
            @click="$emit('toggle-edit-numbers')"
          />
        </p>
      </div>
      <div class="col-sm-2 col-status">
        <span class="data-name font-weight-normal">Estatus:</span>
        <p class="data font-weight-normal">
          <status-badge
            :title="model.isSignedFlag === true ? 'Firmado' : 'No firmado'"
            :type="model.isSignedFlag === true ? 'success' : 'none'"
          />
        </p>
      </div>
      <div class="col-sm">
        <span class="data-name font-weight-normal">
          Fecha Comienzo/Finalización:
        </span>
        <p class="data font-weight-normal">
          {{ getStartEndDate(model) }}
        </p>
      </div>
      <div v-if="model.isDirectPayment" class="col-sm-2 mt-2">
        <span
          class="badge badge-pill badge p-2 px-2"
          :class="`border-color--green`"
          style="background-color: #e3ffe9"
        >
          <em :class="`fas fa-dollar-sign color-green`" />
          <span class="font-weight-bold ml-1" :class="`color-green`">
            Pago Directo
          </span>
        </span>
      </div>
      <div v-else class="col-sm-2">
        <span class="data-name font-weight-normal">Monto:</span>
        <p class="data font-weight-normal">
          {{ model.balanceMaxAmt | price }}
        </p>
      </div>
      <div class="col-sm-1">
        <span v-if="!isChild" class="data-name font-weight-normal">Total:</span>
        <p v-if="!isChild" class="data font-weight-normal">
          {{ totalBalance | price }}
        </p>
      </div>
      <div class="col-sm text-center">
        <drop-down
          :class="{ 'ml-3': isChild }"
          :data-item="model"
          :items="getAvailableActions(model)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import StatusBadge from './StatusBadge.vue';
import CustomInput from 'CustomInput';

export default {
  name: 'RowContractProvider',
  components: {
    StatusBadge,
    CustomInput,
  },
  props: {
    isChild: {
      type: Boolean,
      default: () => false,
    },
    model: {
      type: Object,
      default: () => ({}),
    },
    contractInfo: {
      type: Object,
      default: () => {},
    },
    canEditNumber: {
      type: Boolean,
      default: () => false,
    },
    editingNumbers: {
      type: Boolean,
      default: () => false,
    },
    numberValue: {
      type: String,
      default: () => '',
    },
    numberSuffix: {
      type: String,
      default: () => '',
    },
  },
  computed: {
    getStartEndDate() {
      return (model) => {
        return `${this.$formatDate(
          new Date(
            model.effectiveFromDate ?? this.contractInfo.effectiveFromDate
          )
        )} - ${this.$formatDate(
          new Date(model.effectiveToDate ?? this.contractInfo.effectiveToDate)
        )}`;
      };
    },
    totalBalance() {
      const amendments = this.model.amendments || [];
      return (
        (this.model.balanceMaxAmt || 0) +
        amendments.reduce((sum, a) => sum + (a.balanceMaxAmt || 0), 0)
      );
    },
    canAmendPreviousYear() {
      return this.userHasPermissions('provider.contract.canamendpreviousyear');
    },
    canAmend() {
      return this.userHasPermissions('provider.contract.canamend');
    },
    isSignedFlag() {
      return this.model.isSignedFlag === true;
    },
  },
  methods: {
    showEnmendar(item) {
      let currentAcademicYear = this.$store.state.$_common.currentAcademicYear;

      return (
        this.canAmend &&
        !item.isDirectPayment &&
        !this.isChild &&
        item.isSignedFlag &&
        (currentAcademicYear?.id === this.model.fiscalYearId ||
          this.canAmendPreviousYear)
      );
    },
    getAvailableActions(item) {
      //Problem mapping getter

      let actions = [
        {
          label: 'Entrar',
          icon: 'fas fa-file-import color-primary-1',
          click: this.onEdit,
        },
        {
          label: 'Enmendar',
          icon: 'fas fa-folder color-primary-1',
          visible: this.showEnmendar(item),
          click: this.onAmendment,
        },
        {
          name: 'delete',
          label: 'Eliminar',
          icon: 'fas fa-times-circle color-primary-3',
          click: this.onRemove,
          visible: !item.isSignedFlag,
        },
      ];

      return actions;
    },
    onEdit(item) {
      if (this.isChild) {
        this.onAmendment();
        return;
      }

      this.$router.push({
        name: 'contractAddEdit',
        params: { contractId: item.id },
      });
    },
    onAmendment() {
      this.$router.push({
        name: 'provider-amendment',
        params: {
          companyId: this.model.companyId,
          fiscalYearId: this.model.fiscalYearId,
          contractId: this.model.id,
        },
      });
    },
    onRemove() {
      this.$emit('remove', this.model);
    },
    toogleAmendments() {
      this.model.isOpened = !this.model.isOpened;
      this.$forceUpdate();
    },
  },
};
</script>
<style scoped>
.number-edit-btn {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0 !important;
  line-height: 1;
}

.number-edit-btn ::v-deep i {
  margin: 0 !important;
  line-height: 1;
}

@media (min-width: 576px) {
  .col-contract-number {
    flex: 0 0 20.8333%;
    max-width: 20.8333%;
  }

  .col-status {
    flex: 0 0 12.5%;
    max-width: 12.5%;
  }
}
</style>
